import { snap, route, VELOCITY, type Graph, type SnapPoint, type RouteResult, snapMain } from "@flowsight/sim";

/** Loads graph + snap once; runs the SAME routing engine the API uses (SPEC §6). */
let graph: Graph | null = null;
let snapPts: SnapPoint[] | null = null;

/**
 * Fetch JSON that may or may not be gzip-encoded. On SWA, *.gz is served with
 * content-encoding: gzip and the browser auto-decompresses; on a plain host the
 * bytes arrive raw and we gunzip via DecompressionStream.
 */
async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} → HTTP ${res.status}`);
  const buf = new Uint8Array(await res.arrayBuffer());
  let text: string;
  if (buf.length > 2 && buf[0] === 0x1f && buf[1] === 0x8b && typeof DecompressionStream !== "undefined") {
    const stream = new Blob([buf]).stream().pipeThrough(new DecompressionStream("gzip"));
    text = await new Response(stream).text();
  } else {
    text = new TextDecoder().decode(buf);
  }
  if (text.trimStart().startsWith("<")) throw new Error(`${url} → got HTML (file missing?)`);
  return JSON.parse(text) as T;
}

export async function loadSimData(): Promise<void> {
  const [g, s] = await Promise.all([
    fetchJson<Graph>("/data/graph.json.gz"),
    fetchJson<SnapPoint[]>("/data/snap.json"),
  ]);
  graph = g;
  snapPts = s;
}

/** The loaded routing graph (null until loaded) — read-only use (river names, v0.9.5b). */
export function graphRef(): Graph | null { return graph; }

export function isSimReady(): boolean {
  return graph !== null && snapPts !== null;
}

/** v0.9.5a D2: is a gauge at lon/lat on the same river as the user — on the user's path to the
 *  sea (downstream) or draining into the user's reach (upstream)? Gauge must sit ≤2 km from a reach. */
export function onSameRiver(lon: number, lat: number, userReachId: number, userPath: ReadonlySet<number>): boolean {
  if (!graph || !snapPts) return false;
  const s = snap(snapPts, lon, lat, 2);
  if (!s) return false;
  if (s.id === userReachId || userPath.has(s.id)) return true;
  return route(graph, s, userReachId).reachedDest;
}

/** v0.9.9 C1: the nearest MAIN river (order ≥ 5) within maxKm — the summary's / chip's / pin's
 *  "แม่น้ำใกล้คุณ" (one function for all three). */
export function nearestMainRiver(lon: number, lat: number, maxKm: number): { id: number; distKm: number } | null {
  if (!graph || !snapPts) return null;
  const s = snapMain(snapPts, graph, lon, lat, maxKm);
  return s ? { id: s.id, distKm: s.distKm } : null;
}

/** Snap + route a lon/lat. Returns null if no reach within `maxKm`. */
export function analyze(lon: number, lat: number, maxKm = 25): RouteResult | null {
  if (!graph || !snapPts) return null;
  const s = snap(snapPts, lon, lat, maxKm);
  if (!s) return null;
  return route(graph, s);
}

/** Route from a point to a specific destination reach (e.g. dam → user).
 *  Returns null if not routable; `reachedDest` says if the dest is downstream. */
export function routeTo(lon: number, lat: number, destReachId: number): RouteResult | null {
  if (!graph || !snapPts) return null;
  const s = snap(snapPts, lon, lat, 25);
  if (!s) return null;
  return route(graph, s, destReachId);
}

const vel = (q: number, a: number): number =>
  Math.min(VELOCITY.vMax, Math.max(VELOCITY.vMin, a * Math.pow(Math.max(q, 0.01), VELOCITY.b)));

let coordByReach: Map<number, [number, number]> | null = null;
function reachCoords(): Map<number, [number, number]> {
  if (!coordByReach) {
    coordByReach = new Map();
    for (const [lon, lat, id] of snapPts ?? []) coordByReach.set(id, [lon, lat]);
  }
  return coordByReach;
}

/** Reach ids from `id` downstream (next_down), at most `max` reaches. */
export function downstreamIds(id: number, max: number): number[] {
  const out: number[] = [];
  for (let cur = id, i = 0; cur && graph && i < max; i++) { out.push(cur); cur = graph[String(cur)]?.[0] ?? 0; }
  return out;
}

/** [reachId, representative point] along a path (reaches without a snap point skipped). */
export function pathReachPoints(path: number[]): [number, [number, number]][] {
  if (!snapPts) return [];
  const cm = reachCoords();
  const out: [number, [number, number]][] = [];
  for (const id of path) { const c = cm.get(id); if (c) out.push([id, c]); }
  return out;
}

/** Polyline (one representative point per reach, from snap.json) for a reach-id
 *  path, plus cumulative travel SECONDS to the start of each vertex for the fast
 *  and slow velocity bounds (same law the ETA uses). Drives the ▶ animation:
 *  midpoint timing positions the water head; fast/slow give the "+X–Y วัน" range. */
export function pathGeometry(path: number[]): { coords: [number, number][]; cumSecFast: number[]; cumSecSlow: number[] } | null {
  if (!graph || !snapPts) return null;
  const cm = reachCoords();
  const coords: [number, number][] = [], cumSecFast: number[] = [], cumSecSlow: number[] = [];
  let sf = 0, ss = 0;
  for (const id of path) {
    const c = cm.get(id);
    if (!c) continue;
    coords.push(c);
    cumSecFast.push(sf);
    cumSecSlow.push(ss);
    const t = graph[String(id)];
    if (t) { const lengthM = t[1] * 1000, q = t[2]; sf += lengthM / vel(q, VELOCITY.aFast); ss += lengthM / vel(q, VELOCITY.aSlow); }
  }
  return coords.length ? { coords, cumSecFast, cumSecSlow } : null;
}
